import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProgramPreferencesService } from '../services/userProgramPreferencesService';
import { progressService } from '../services/progressService';
import { useDialog } from '../context/DialogContext';
import styles from '../styles/authenticatedHome.module.css';

interface ProgramInfo {
  id: string;
  name: string;
  initials: string;
}

const PROGRAMS: ProgramInfo[] = [
  { id: 'saa', name: 'SAA - Sex Addicts Anonymous', initials: 'SAA' },
  { id: 'aa', name: 'AA - Alcoholics Anonymous', initials: 'AA' },
  { id: 'na', name: 'NA - Narcotics Anonymous', initials: 'NA' },
  { id: 'oa', name: 'OA - Overeaters Anonymous', initials: 'OA' },
  { id: 'ga', name: 'GA - Gamblers Anonymous', initials: 'GA' },
  { id: 'da', name: 'DA - Debtors Anonymous', initials: 'DA' },
  { id: 'coda', name: 'CoDA - Co-Dependents Anonymous', initials: 'CoDA' },
  { id: 'ca', name: 'CA - Cocaine Anonymous', initials: 'CA' }
];

export default function AuthenticatedHome() {
  const navigate = useNavigate();
  const { showAlert } = useDialog();
  const [primaryProgram, setPrimaryProgram] = useState<string | null>(null);
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUserData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get user's program preferences - both selected and primary
      const preferences = await UserProgramPreferencesService.getUserProgramPreferences();
      const program = preferences?.primaryProgram;
      const programs = preferences?.selectedPrograms || [];

      if (!program || programs.length === 0) {
        // No programs set, show setup prompt
        setPrimaryProgram(null);
        setSelectedPrograms([]);
        setIsLoading(false);
        return;
      }

      setPrimaryProgram(program);
      setSelectedPrograms(programs);

      // Get current step for the primary program
      await loadCurrentStep(program);
    } catch (err) {
      console.error('Error loading user data:', err);
      setError("We're having trouble loading your progress right now. Please try again in a moment.");
    } finally {
      setIsLoading(false);
    }
  };

  const [currentStepStatus, setCurrentStepStatus] = useState<string>('not-started');
  const [stepProgress, setStepProgress] = useState<Record<number, any>>({});

  // Function to determine step status based on progress data directly (same logic as WorkingStepsPage)
  const getStepStatusFromData = (progress: any): 'not-started' | 'in-progress' | 'completed' => {
    if (!progress) return 'not-started';
    
    if (progress.completed) return 'completed';
    
    // Check for completed actions - this is the ONLY way a step can be "in progress"
    if (progress.data && progress.data.actions && Array.isArray(progress.data.actions)) {
      const hasCompletedAction = progress.data.actions.some((action: any) => action.completed);
      if (hasCompletedAction) return 'in-progress';
    }
    
    // If no completed actions, it's not started (regardless of other data)
    return 'not-started';
  };

  // Function to determine step status based on progress state (for use in render)
  const getStepStatus = (stepNumber: number): 'not-started' | 'in-progress' | 'completed' => {
    const progress = stepProgress[stepNumber];
    return getStepStatusFromData(progress);
  };

  const loadCurrentStep = async (program: string) => {
    try {
      // Only SAA has full 12-step implementation
      if (program !== 'saa') {
        setCurrentStep(null);
        setCurrentStepStatus('not-started');
        return;
      }

      // Use uppercase 'SAA' to match the backend data format (same as WorkingStepsPage)
      const progress = await progressService.getAllStepProgress('SAA', '12-steps');
      setStepProgress(progress);
      
      // Find the first step that is not completed (either not started or in progress)
      let currentStep = 1;
      let stepStatus = 'not-started';
      
      for (let stepNum = 1; stepNum <= 12; stepNum++) {
        // Use the progress data directly instead of relying on state (which is async)
        const stepProgress = progress[stepNum];
        const status = getStepStatusFromData(stepProgress);
        
        if (status !== 'completed') {
          currentStep = stepNum;
          stepStatus = status;
          break; // Found the first incomplete step
        }
      }
      
      setCurrentStep(currentStep);
      setCurrentStepStatus(stepStatus);
    } catch (err) {
      console.error('Error loading current step:', err);
      setCurrentStep(1); // Default to step 1 if there's an error
      setCurrentStepStatus('not-started');
    }
  };

  const handleStepClick = (stepNumber: number) => {
    if (primaryProgram === 'saa') {
      navigate(`/saa/12-steps/step-${stepNumber}`);
    } else {
      showUnderConstructionModal();
    }
  };

  const handleProgramClick = (path: string) => {
    if (primaryProgram === 'saa') {
      navigate(path);
    } else {
      showUnderConstructionModal();
    }
  };

  const showUnderConstructionModal = () => {
    const programInfo = PROGRAMS.find(p => p.id === primaryProgram);
    const programName = programInfo?.name || 'This program';
    
    showAlert({
      title: 'Coming Soon',
      message: `${programName} 12-step tools are currently under development. We're working hard to bring you the same comprehensive recovery experience for all programs.`
    });
  };

  const handleAssessmentClick = () => {
    navigate('/self-assess');
  };

  const handleDiscoverClick = () => {
    navigate('/discover');
  };

  const handleChangePrimaryProgram = () => {
    navigate('/onboarding');
  };

  const handleAddAnotherProgram = () => {
    navigate('/onboarding');
  };

  const handleMakePrimary = async (programId: string) => {
    try {
      await UserProgramPreferencesService.setPrimaryProgram(programId);
      await loadUserData(); // Reload to show new primary
    } catch (error) {
      console.error('Error setting primary program:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to update primary program. Please try again.'
      });
    }
  };

  const handleRemoveProgram = async (programId: string) => {
    const prog = PROGRAMS.find(p => p.id === programId);
    const programName = prog?.name || programId;

    // Don't allow removing if it's the only program
    if (selectedPrograms.length === 1) {
      showAlert({
        title: 'Cannot Remove',
        message: 'You must have at least one program selected. Add another program first, then you can remove this one.'
      });
      return;
    }

    try {
      await UserProgramPreferencesService.removeProgramFromPreferences(programId);
      await loadUserData(); // Reload to update the display
    } catch (error) {
      console.error('Error removing program:', error);
      showAlert({
        title: 'Error',
        message: `Failed to remove ${programName}. Please try again.`
      });
    }
  };

  const handleViewProgram = (programId: string) => {
    navigate(`/${programId}`);
  };

  const getSecondaryPrograms = () => {
    return selectedPrograms.filter(p => p !== primaryProgram);
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <h1>Personal Dashboard</h1>
        <p>Welcome back and keep going!</p>
        <div className={styles.loadingMessage}>
          <p>Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <h1>Personal Dashboard</h1>
        <p>Welcome back and keep going!</p>
        <div className={styles.errorMessage}>
          <p>{error}</p>
          <button onClick={loadUserData} className={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show setup prompt if no primary program is set
  if (!primaryProgram) {
    return (
      <div className={styles.container}>
        <h1>Personal Dashboard</h1>
        <p>Welcome back and keep going!</p>
        
        <div className={styles.setupPrompt}>
          <h2>Select Your Primary Recovery Program</h2>
          <p>Choose the recovery program that best fits your needs to get started.</p>
          <button 
            onClick={() => navigate('/onboarding')}
            className={styles.primaryButton}
          >
            Select My Program
          </button>
        </div>

        <div className={styles.actionCards}>
          <div className={styles.actionCard}>
            <h3>Assess</h3>
            <p>Am I an addict?</p>
            <button 
              onClick={handleAssessmentClick}
              className={styles.actionButton}
            >
              Take Self-Assessment
            </button>
          </div>
          
          <div className={styles.actionCard}>
            <h3>Discover</h3>
            <p>Learn about addictions and recovery programs</p>
            <button 
              onClick={handleDiscoverClick}
              className={styles.actionButton}
            >
              Explore Programs
            </button>
          </div>
        </div>
      </div>
    );
  }

  const programInfo = PROGRAMS.find(p => p.id === primaryProgram);
  const programInitials = programInfo?.initials || primaryProgram?.toUpperCase() || '';

  const getStepButtonText = () => {
    if (currentStepStatus === 'in-progress') {
      return `Continue Step ${currentStep}`;
    } else {
      return `Start Step ${currentStep}`;
    }
  };

  const secondaryPrograms = getSecondaryPrograms();

  return (
    <div className={styles.container}>
      <h1>Personal Dashboard</h1>
      <p>Welcome back and keep going!</p>

      <div className={styles.mainContent}>
        {/* PRIMARY PROGRAM - Featured */}
        <div className={styles.primaryProgramSection}>
          <div className={styles.primaryBadge}>⭐ Primary Program</div>
          <div className={styles.unifiedContentBox}>
            {/* Program Initials inside the box */}
            <div className={styles.programInitials}>{programInitials}</div>
            
            {/* Progress Button */}
            <div className={styles.progressSection}>
              <button 
                onClick={() => handleProgramClick(`/${primaryProgram}/12-steps`)}
                className={styles.progressButton}
              >
                My {programInitials} 12 Step Progress
              </button>
            </div>
            
            {/* Current Step Button */}
            {currentStep && (
              <div className={styles.stepSection}>
                <button 
                  onClick={() => handleStepClick(currentStep)}
                  className={styles.stepButton}
                >
                  {getStepButtonText()}
                </button>
              </div>
            )}
            
            {/* Resources Section */}
            <div className={styles.resourcesSection}>
              <button 
                onClick={() => handleProgramClick(`/${primaryProgram}`)}
                className={styles.resourcesButton}
              >
                More {programInitials} Resources
              </button>
            </div>
          </div>
        </div>

        {/* SECONDARY PROGRAMS - Compact Cards */}
        {secondaryPrograms.length > 0 && (
          <div className={styles.secondaryProgramsSection}>
            <h3 className={styles.secondaryProgramsTitle}>Other Programs</h3>
            <div className={styles.secondaryProgramsGrid}>
              {secondaryPrograms.map(progId => {
                const prog = PROGRAMS.find(p => p.id === progId);
                if (!prog) return null;
                // Extract just the full name without the abbreviation
                // e.g., "OA - Overeaters Anonymous" becomes "Overeaters Anonymous"
                const fullName = prog.name.includes(' - ') 
                  ? prog.name.split(' - ')[1] 
                  : prog.name;
                return (
                  <div key={progId} className={styles.secondaryProgramCard}>
                    <div className={styles.secondaryProgramHeader}>
                      <div className={styles.secondaryProgramInfo}>
                        <span className={styles.secondaryProgramInitials}>{prog.initials}</span>
                        <span className={styles.secondaryProgramName}>{fullName}</span>
                      </div>
                      <button 
                        onClick={() => handleRemoveProgram(progId)}
                        className={styles.removeButton}
                        title="Remove this program"
                      >
                        ×
                      </button>
                    </div>
                    <div className={styles.secondaryProgramActions}>
                      <button 
                        onClick={() => handleViewProgram(progId)}
                        className={styles.secondaryButton}
                      >
                        View Resources
                      </button>
                      <button 
                        onClick={() => handleMakePrimary(progId)}
                        className={styles.secondaryButtonAlt}
                      >
                        Make Primary
                      </button>
                    </div>
                  </div>
                );
              })}
              
              {/* Add Program Card */}
              <div className={styles.secondaryProgramCard} style={{ cursor: 'pointer' }} onClick={handleAddAnotherProgram}>
                <div className={styles.addProgramCard}>
                  <div className={styles.addProgramIcon}>+</div>
                  <div className={styles.addProgramText}>Add Program</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show Add Program button if no secondary programs */}
        {secondaryPrograms.length === 0 && (
          <div className={styles.addProgramPrompt}>
            <button 
              onClick={handleAddAnotherProgram}
              className={styles.addProgramButton}
            >
              + Add Another Program
            </button>
          </div>
        )}

        {/* Action Cards - Assess and Discover on same row */}
        <div className={styles.actionCardsRow}>
          <div className={styles.actionCard}>
            <h3>Assess</h3>
            <p>Am I an addict?</p>
            <button 
              onClick={handleAssessmentClick}
              className={styles.actionButton}
            >
              Take Self-Assessment
            </button>
          </div>
          
          <div className={styles.actionCard}>
            <h3>Discover</h3>
            <p>Learn about addictions and recovery programs</p>
            <button 
              onClick={handleDiscoverClick}
              className={styles.actionButton}
            >
              Explore Programs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
